import React, { useState, useRef, useCallback } from 'react';
import { useModelContext } from './ModelContext';
import TailwindSelect from '../Tailwind/TailwindSelect';
import TailwindModal from '../Tailwind/TailwindModal';
import TailwindButton from '../Tailwind/TailwindButton';
import { useEditorStore } from '../../shared/stores/useEditorStore';
import { useChatOpen, useSharedChat } from '../../hooks/useAgentContext';
import apiService from '../../services/ApiService';
import NewScenarioModal from '../NewScenarioModal';
import NewSensitivityModal from '../NewSensitivityModal';
import type { ModelScenarioMeta, Sensitivity } from '../../shared/types/model';

export default function ScenarioSensitivitySelector() {
    const { model, currentScenario, currentSensitivity, setCurrentScenario, setCurrentSensitivity, refreshModel } = useModelContext();
    const { userRights } = useEditorStore();
    const { setIsChatOpen } = useChatOpen();
    const { sendMessage } = useSharedChat();

    const [renamingScenario, setRenamingScenario] = useState(false);
    const [scenarioRenameValue, setScenarioRenameValue] = useState('');
    const [renamingSensitivity, setRenamingSensitivity] = useState(false);
    const [sensitivityRenameValue, setSensitivityRenameValue] = useState('');
    const [newScenarioModalOpen, setNewScenarioModalOpen] = useState(false);
    const [newSensitivityModalOpen, setNewSensitivityModalOpen] = useState(false);
    const [scenarioDeleteOpen, setScenarioDeleteOpen] = useState(false);
    const [sensitivityDeleteOpen, setSensitivityDeleteOpen] = useState(false);
    const scenarioRenameSubmittedRef = useRef(false);
    const sensitivityRenameSubmittedRef = useRef(false);
    const scenarioInputRef = useRef<HTMLInputElement>(null);
    const sensitivityInputRef = useRef<HTMLInputElement>(null);

    if (!model || !model.modelScenarios || model.modelScenarios.length === 0) {
        return null;
    }

    const scenarioOptions = model.modelScenarios.map((s: ModelScenarioMeta) => ({
        value: s.id.toString(),
        label: s.name,
    }));

    const sensitivities: Sensitivity[] = currentScenario?.sensitivities || [];
    const sensitivityOptions = [
        { value: 'null', label: 'No Sensitivity' },
        ...sensitivities.map((s: Sensitivity) => ({
            value: s.id.toString(),
            label: s.name,
        })),
    ];

    const handleScenarioChange = (value: string) => {
        const scenario = model.modelScenarios.find((s: ModelScenarioMeta) => s.id.toString() === value);
        if (scenario) {
            setCurrentScenario(scenario);
        }
    };

    const handleSensitivityChange = (value: string) => {
        if (value === 'null') {
            setCurrentSensitivity(null);
        } else {
            const sensitivity = sensitivities.find((s: Sensitivity) => s.id.toString() === value);
            if (sensitivity) {
                setCurrentSensitivity(sensitivity);
            }
        }
    };

    const startRenamingScenario = () => {
        if (!currentScenario) return;
        scenarioRenameSubmittedRef.current = false;
        setScenarioRenameValue(currentScenario.name);
        setRenamingScenario(true);
    };

    const handleRenameScenario = () => {
        if (scenarioRenameSubmittedRef.current) return;
        if (!currentScenario || !scenarioRenameValue.trim()) {
            setRenamingScenario(false);
            return;
        }
        scenarioRenameSubmittedRef.current = true;
        apiService.putModelScenario(currentScenario.id, scenarioRenameValue.trim())
            .then(() => refreshModel())
            .catch((err: unknown) => console.error('Failed to rename scenario:', err));
        setRenamingScenario(false);
    };

    const handleDeleteScenario = () => {
        if (!currentScenario) return;
        setScenarioDeleteOpen(true);
    };

    const confirmDeleteScenario = () => {
        if (!currentScenario) return;
        apiService.deleteModelScenario(currentScenario.id)
            .then(async (response) => {
                if (response) {
                    const remaining = model.modelScenarios.filter((s: ModelScenarioMeta) => s.id !== currentScenario.id);
                    if (remaining.length > 0) {
                        setCurrentScenario(remaining[0]);
                    }
                    setCurrentSensitivity(null);
                    setScenarioDeleteOpen(false);
                    await refreshModel();
                }
            })
            .catch((err: unknown) => console.error('Failed to delete scenario:', err));
    };

    const startRenamingSensitivity = () => {
        if (!currentSensitivity) return;
        sensitivityRenameSubmittedRef.current = false;
        setSensitivityRenameValue(currentSensitivity.name);
        setRenamingSensitivity(true);
    };

    const handleRenameSensitivity = () => {
        if (sensitivityRenameSubmittedRef.current) return;
        if (!currentSensitivity || !sensitivityRenameValue.trim()) {
            setRenamingSensitivity(false);
            return;
        }
        sensitivityRenameSubmittedRef.current = true;
        apiService.putSensitivity(currentSensitivity.id, sensitivityRenameValue.trim())
            .then(() => refreshModel())
            .catch((err: unknown) => console.error('Failed to rename sensitivity:', err));
        setRenamingSensitivity(false);
    };

    const handleDeleteSensitivity = () => {
        if (!currentSensitivity) return;
        setSensitivityDeleteOpen(true);
    };

    const confirmDeleteSensitivity = () => {
        if (!currentSensitivity) return;
        apiService.deleteSensitivity(currentSensitivity.id)
            .then(async (response) => {
                if (response) {
                    setCurrentSensitivity(null);
                    setSensitivityDeleteOpen(false);
                    await refreshModel();
                }
            })
            .catch((err: unknown) => console.error('Failed to delete sensitivity:', err));
    };

    const handleSensitivityInfo = useCallback(() => {
        setIsChatOpen(true);
        sendMessage("Explain me what is a Sensitivity in Peerception and how to use it ?");
    }, [setIsChatOpen, sendMessage]);

    const handleScenarioCreated = () => {
        refreshModel();
    };

    const isScenarioLimitReached = model.modelRulesDeducted?.number_scenario_max_per_model !== -1
        && model.modelRulesDeducted?.number_scenario_max_per_model - model.modelScenarios.length <= 0;

    const isSensitivityLimitReached = model.modelRulesDeducted?.number_sensitivities_max_per_scenario !== -1
        && model.modelRulesDeducted?.number_sensitivities_max_per_scenario - (currentScenario?.sensitivities?.length ?? 0) <= 0;

    return (
        <>
            <div className="flex items-center gap-2 flex-wrap">
                {/* Scenario Selector */}
                <div className="flex flex-col">
                    <div style={{ minWidth: 200 }}>
                        {renamingScenario ? (
                            <div className="flex items-center gap-1 mt-4">
                                <input
                                    ref={scenarioInputRef}
                                    autoFocus
                                    type="text"
                                    value={scenarioRenameValue}
                                    onChange={(e) => setScenarioRenameValue(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') scenarioInputRef.current?.blur();
                                        if (e.key === 'Escape') {
                                            scenarioRenameSubmittedRef.current = true;
                                            setRenamingScenario(false);
                                        }
                                    }}
                                    onBlur={handleRenameScenario}
                                    className="w-full border border-slate-300 rounded px-2 py-1 text-sm focus:outline-none focus:border-slate-900"
                                />
                            </div>
                        ) : (
                            <TailwindSelect
                                label="Scenario"
                                value={currentScenario?.id?.toString() || ''}
                                onChange={handleScenarioChange}
                                options={scenarioOptions}
                                placeholder="Select scenario"
                            />
                        )}
                    </div>
                    {userRights.right_to_write && (
                        <div className="flex items-center gap-0.5 mt-1">
                            <button
                                onClick={() => setNewScenarioModalOpen(true)}
                                disabled={isScenarioLimitReached}
                                className="flex items-center justify-center p-0.5 rounded hover:bg-slate-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                title="New scenario"
                            >
                                <span className="material-symbols-outlined text-[16px] leading-none text-slate-400 hover:text-slate-600">add</span>
                            </button>
                            <button
                                onClick={startRenamingScenario}
                                className="flex items-center justify-center p-0.5 rounded hover:bg-slate-100 transition-colors"
                                title="Rename scenario"
                            >
                                <span className="material-symbols-outlined text-[16px] leading-none text-slate-400 hover:text-slate-600">edit</span>
                            </button>
                            {model.modelScenarios.length > 1 && (
                                <button
                                    onClick={handleDeleteScenario}
                                    className="flex items-center justify-center p-0.5 rounded hover:bg-red-50 transition-colors"
                                    title="Delete scenario"
                                >
                                    <span className="material-symbols-outlined text-[16px] leading-none text-slate-400 hover:text-red-500">delete</span>
                                </button>
                            )}
                        </div>
                    )}
                </div>

                {/* Sensitivity Selector */}
                <div className="flex flex-col">
                    <div style={{ minWidth: 200 }}>
                        {renamingSensitivity && currentSensitivity ? (
                            <div className="flex items-center gap-1 mt-4">
                                <input
                                    ref={sensitivityInputRef}
                                    autoFocus
                                    type="text"
                                    value={sensitivityRenameValue}
                                    onChange={(e) => setSensitivityRenameValue(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') sensitivityInputRef.current?.blur();
                                        if (e.key === 'Escape') {
                                            sensitivityRenameSubmittedRef.current = true;
                                            setRenamingSensitivity(false);
                                        }
                                    }}
                                    onBlur={handleRenameSensitivity}
                                    className="w-full border border-slate-300 rounded px-2 py-1 text-sm focus:outline-none focus:border-slate-900"
                                />
                            </div>
                        ) : (
                            <TailwindSelect
                                label="Sensitivity"
                                value={currentSensitivity ? currentSensitivity.id.toString() : 'null'}
                                onChange={handleSensitivityChange}
                                options={sensitivityOptions}
                                placeholder="Select sensitivity"
                            />
                        )}
                    </div>
                    <div className="flex items-center gap-0.5 mt-1">
                        {userRights.right_to_write && (
                            <>
                                <button
                                    onClick={() => setNewSensitivityModalOpen(true)}
                                    disabled={isSensitivityLimitReached}
                                    className="flex items-center justify-center p-0.5 rounded hover:bg-slate-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                    title="New sensitivity"
                                >
                                    <span className="material-symbols-outlined text-[16px] leading-none text-slate-400 hover:text-slate-600">add</span>
                                </button>
                                {currentSensitivity && (
                                    <button
                                        onClick={startRenamingSensitivity}
                                        className="flex items-center justify-center p-0.5 rounded hover:bg-slate-100 transition-colors"
                                        title="Rename sensitivity"
                                    >
                                        <span className="material-symbols-outlined text-[16px] leading-none text-slate-400 hover:text-slate-600">edit</span>
                                    </button>
                                )}
                                {currentSensitivity && (
                                    <button
                                        onClick={handleDeleteSensitivity}
                                        className="flex items-center justify-center p-0.5 rounded hover:bg-red-50 transition-colors"
                                        title="Delete sensitivity"
                                    >
                                        <span className="material-symbols-outlined text-[16px] leading-none text-slate-400 hover:text-red-500">delete</span>
                                    </button>
                                )}
                            </>
                        )}
                        <button
                            onClick={handleSensitivityInfo}
                            className="flex items-center justify-center p-0.5 rounded hover:bg-slate-100 transition-colors"
                            title="What is a sensitivity?"
                        >
                            <span className="material-symbols-outlined text-[16px] leading-none text-slate-400 hover:text-slate-600">help_outline</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Delete Scenario Modal */}
            <TailwindModal
                isOpen={scenarioDeleteOpen}
                onClose={() => setScenarioDeleteOpen(false)}
                title="Are you sure?"
                size="md"
                footer={
                    <div className="flex gap-3 justify-center w-full">
                        <TailwindButton
                            variant="secondary"
                            onClick={() => setScenarioDeleteOpen(false)}
                            icon="close"
                        >
                            Cancel
                        </TailwindButton>
                        <TailwindButton
                            variant="danger"
                            onClick={confirmDeleteScenario}
                            icon="delete_forever"
                        >
                            Yes, Delete
                        </TailwindButton>
                    </div>
                }
            >
                <p className="text-center text-slate-700">
                    This will delete <strong>{currentScenario?.name}</strong> and all its content permanently.
                </p>
            </TailwindModal>

            {/* Delete Sensitivity Modal */}
            <TailwindModal
                isOpen={sensitivityDeleteOpen}
                onClose={() => setSensitivityDeleteOpen(false)}
                title="Are you sure?"
                size="md"
                footer={
                    <div className="flex gap-3 justify-center w-full">
                        <TailwindButton
                            variant="secondary"
                            onClick={() => setSensitivityDeleteOpen(false)}
                            icon="close"
                        >
                            Cancel
                        </TailwindButton>
                        <TailwindButton
                            variant="danger"
                            onClick={confirmDeleteSensitivity}
                            icon="delete_forever"
                        >
                            Yes, Delete
                        </TailwindButton>
                    </div>
                }
            >
                <p className="text-center text-slate-700">
                    This will delete <strong>{currentSensitivity?.name}</strong> and all its content permanently.
                </p>
            </TailwindModal>

            {/* Modals */}
            <NewScenarioModal
                modelScenarios={model.modelScenarios}
                modelId={model.id}
                newScenarioModalOpen={newScenarioModalOpen}
                setNewScenarioModalOpen={setNewScenarioModalOpen}
                setCurrentScenario={(scenario: ModelScenarioMeta) => {
                    setCurrentScenario(scenario);
                    handleScenarioCreated();
                }}
                setSocketUrl={() => {/* WebSocket managed by ModelContext */}}
            />
            <NewSensitivityModal
                sensitivities={sensitivities}
                scenarioId={currentScenario?.id}
                newSensitivityModalOpen={newSensitivityModalOpen}
                setNewSensitivityModalOpen={setNewSensitivityModalOpen}
                setCurrentSensitivity={(sensitivity: Sensitivity) => {
                    setCurrentSensitivity(sensitivity);
                    refreshModel();
                }}
            />
        </>
    );
}
